import { Calibration, formatBRL, formatIsoDate } from '@deizi/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../../lib/api';

const pct = (bps: number) =>
  `${bps > 0 ? '+' : ''}${(bps / 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

/** "Calibração" (board Canais): fees predicted × charged, from orders whose real charges were typed. */
export function CalibrationCard({ onReview }: { onReview: (channelId: string) => void }) {
  const cal = useQuery({
    queryKey: ['channels', 'calibration'],
    queryFn: () => api.get('/channels/calibration', Calibration),
  });
  if (!cal.data) return null;
  const rows = cal.data.channels.filter((c) => !c.few);
  const few = cal.data.channels.filter((c) => c.few);
  const worst = rows
    .filter((c) => c.diffBps !== null && Math.abs(c.diffBps) > 100)
    .toSorted((a, b) => Math.abs(b.diffBps!) - Math.abs(a.diffBps!))[0];
  const cols = '1.4fr 1fr 1fr 90px 90px';
  return (
    <section className="card flat" style={{ marginTop: 18 }}>
      <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 8 }}>
        <h2>Calibração</h2>
        <span className="xs muted">
          tarifas previstas × cobradas, de pedidos lançados à mão desde{' '}
          {formatIsoDate(cal.data.from)}
        </span>
      </div>
      <div className="gt" style={{ overflowX: 'auto' }}>
        <div className="gtr head" style={{ gridTemplateColumns: cols, minWidth: 560 }}>
          <span>Canal</span>
          <span className="num">Previsto</span>
          <span className="num">Real</span>
          <span className="num">Diferença</span>
          <span />
        </div>
        {rows.map((c) => {
          const off = c.diffBps !== null && Math.abs(c.diffBps) > 100;
          return (
            <div
              key={c.channelId}
              className="gtr"
              style={{ gridTemplateColumns: cols, minWidth: 560 }}
            >
              <span className="row" style={{ gap: 8 }}>
                <span className="dot" style={{ background: c.color ?? 'var(--line)' }} />
                {c.name}
                <span className="xs muted">· {c.orders} pedidos</span>
              </span>
              <span className="num">{formatBRL(c.predictedCentavos)}</span>
              <span className="num">{formatBRL(c.realCentavos)}</span>
              <span
                className="num"
                style={off ? { color: 'var(--warn)', fontWeight: 600 } : undefined}
              >
                {c.diffBps === null ? '—' : pct(c.diffBps)}
              </span>
              <span className="num">
                {off ? (
                  <button type="button" className="btn sm" onClick={() => onReview(c.channelId)}>
                    Revisar
                  </button>
                ) : null}
              </span>
            </div>
          );
        })}
        {few.length ? (
          <div className="gtr" style={{ gridTemplateColumns: cols, minWidth: 560 }}>
            <span className="sm muted">{few.map((c) => c.name).join(', ')}</span>
            <span className="num muted">—</span>
            <span className="num muted">—</span>
            <span className="num xs muted">poucos pedidos</span>
            <span />
          </div>
        ) : null}
      </div>
      <p className="sm" style={{ padding: '10px 18px 16px' }}>
        {worst ? (
          <>
            O {worst.name} cobrou{' '}
            {formatBRL(Math.abs(worst.realCentavos - worst.predictedCentavos))}{' '}
            {worst.realCentavos > worst.predictedCentavos ? 'a mais' : 'a menos'} que o previsto.
            Diferenças acima de 1% costumam ser frete ou tarifa nova: confira a versão das tarifas.{' '}
            <Link to={`/pedidos?canal=${worst.channelId}`}>Ver pedidos</Link>
          </>
        ) : (
          <span className="muted">
            Conta só pedidos com as cobranças reais digitadas (comissão, tarifa fixa e frete). Com
            {` ${cal.data.minOrders}`} ou mais por canal, a diferença aparece aqui.
          </span>
        )}
      </p>
    </section>
  );
}
